/**
 * ч.1313: «Відповідальний» за документом / контрагентом і передача іншому користувачу.
 *
 * Межі записів «лише свої» (ч.1312) рахують відповідального. Показуємо його в документі
 * (картка правої колонки або рядок статусу поруч із «дт-кт») і в картці контрагента;
 * власник / адміністратор акаунта передає запис іншому — лише тим, хто має доступ до
 * організації запису (список кандидатів дає сервер). Види з відповідальним — з довідника
 * видів записів (`/access-roles/record-scope-kinds`, поле `tables`).
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accessRoles } from '@/api/admin';
import { recordOwners, type RecordOwnerInfo } from '@/api/recordOwners';
import { humanizeBackendError } from '@/utils/backendError';
import BodyPortal from './BodyPortal';

export interface OwnerDocRef { type: string; id?: string | null }

/** Таблиці, у записів яких є відповідальний. */
export function useOwnerTables(): Set<string> {
  const q = useQuery({
    queryKey: ['record-scope-kinds'],
    queryFn: () => accessRoles.recordScopeKinds(),
    staleTime: Infinity,
  });
  return useMemo(() => new Set(Array.isArray(q.data)
    ? q.data.flatMap((k) => (Array.isArray(k?.tables) ? k.tables : [])) : []), [q.data]);
}

function useOwner(doc: OwnerDocRef | undefined) {
  const tables = useOwnerTables();
  const id = doc?.id && doc.id !== 'new' ? doc.id : undefined;
  const enabled = !!doc && !!id && tables.has(doc.type);
  const q = useQuery({
    queryKey: ['record-owner', doc?.type, id],
    queryFn: () => recordOwners.owner(doc!.type, id!),
    enabled,
    retry: false,
  });
  // Відповідь без очікуваної форми (заглушка API в тестах сторінок) — блок не показуємо.
  const info = q.data && typeof q.data === 'object' && 'can_transfer' in q.data ? q.data : undefined;
  return { enabled, id, info };
}

function ownerName(info: RecordOwnerInfo): string {
  if (info.user_id) return info.full_name || info.email || '—';
  return info.imported ? 'не вказано (з 1С)' : 'не вказано';
}

/** Рядок статусу документа / картки: «відповідальний: Ім'я · передати». */
export function RecordOwnerInline({ doc }: { doc?: OwnerDocRef }) {
  const { enabled, id, info } = useOwner(doc);
  const [open, setOpen] = useState(false);
  if (!enabled || !info) return null;
  return (
    <span data-testid="record-owner" title="Відповідальний за запис: його бачать ролі з межею «лише свої записи»"
      style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
      відповідальний: <b style={{ color: 'var(--text)' }}>{ownerName(info)}</b>
      {info.can_transfer && (
        <>
          {' '}
          <button type="button" className="btn-link" onClick={() => setOpen(true)}
            data-testid="record-owner-transfer">передати</button>
        </>
      )}
      {open && <TransferOwnerDialog doc={{ type: doc!.type, id: id! }} info={info} onClose={() => setOpen(false)} />}
    </span>
  );
}

/** Картка правої колонки документа (DocRail). */
export function RecordOwnerCard({ doc }: { doc?: OwnerDocRef }) {
  const { enabled, id, info } = useOwner(doc);
  const [open, setOpen] = useState(false);
  if (!enabled || !info) return null;
  return (
    <div className="docrail__card" data-testid="doc-owner-card">
      <div className="docrail__title">Відповідальний</div>
      <div className="docrail__row">
        <div>{ownerName(info)}</div>
        {info.user_id && info.email && info.full_name && (
          <div style={{ color: 'var(--muted)' }}>{info.email}</div>
        )}
        {!info.user_id && info.imported && info.can_transfer && (
          <div style={{ color: 'var(--muted)' }}>
            зіставте користувачів 1С: «Адміністрування → Відповідальні»
          </div>
        )}
      </div>
      {info.can_transfer && (
        <button type="button" className="docrail__more" onClick={() => setOpen(true)}
          data-testid="record-owner-transfer">передати іншому →</button>
      )}
      {open && <TransferOwnerDialog doc={{ type: doc!.type, id: id! }} info={info} onClose={() => setOpen(false)} />}
    </div>
  );
}

function TransferOwnerDialog({ doc, info, onClose }: {
  doc: { type: string; id: string };
  info: RecordOwnerInfo;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const options = info.candidates.filter((c) => c.user_id !== info.user_id);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const mut = useMutation({
    mutationFn: () => recordOwners.transfer(doc.type, [doc.id], to),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['record-owner', doc.type, doc.id] });
      qc.invalidateQueries({ queryKey: ['doc-history', doc.type, doc.id] });
      onClose();
    },
    onError: (e) => setError(humanizeBackendError(e)),
  });
  return (
    <BodyPortal>
      <div role="dialog" aria-modal="true" aria-label="Передати іншому відповідальному"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 60,
          display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div onClick={(e) => e.stopPropagation()} data-testid="record-owner-dialog"
          style={{ background: 'var(--bg-panel)', color: 'var(--text)', borderRadius: 'var(--radius)',
            padding: '1.25rem 1.5rem', width: 460, maxWidth: '92vw' }}>
          <h3 style={{ marginTop: 0 }}>Передати іншому відповідальному</h3>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            Зараз: <b style={{ color: 'var(--text)' }}>{ownerName(info)}</b>. Новий відповідальний
            бачитиме запис, навіть якщо його роль показує лише власні записи. У переліку — лише ті,
            хто має доступ до організації запису.
          </div>
          <div className="field">
            <label htmlFor="record-owner-to">Кому</label>
            <select id="record-owner-to" value={to} onChange={(e) => setTo(e.target.value)}>
              <option value="">— оберіть користувача —</option>
              {options.map((c) => (
                <option key={c.user_id} value={c.user_id}>{c.full_name || c.email} — {c.email}</option>
              ))}
            </select>
          </div>
          {error && <div className="error" style={{ marginTop: '0.5rem' }}>{error}</div>}
          <div className="drawer__actions" style={{ marginTop: '1rem' }}>
            <button type="button" onClick={onClose}>скасувати</button>
            <button type="button" className="primary" disabled={!to || mut.isPending}
              onClick={() => { setError(null); mut.mutate(); }}>
              {mut.isPending ? 'передаємо…' : 'передати'}
            </button>
          </div>
        </div>
      </div>
    </BodyPortal>
  );
}
